import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import CompassLayout from "@/components/compass/CompassLayout";
import { READINESS_COLORS, SCORE_TONE, COUNTRY_FLAGS } from "@/data/compass";
import { useCompass } from "@/context/CompassContext";
import {
  ArrowRight, Compass, Target, Warning, Lightbulb, Sparkle, Path, Eye,
} from "@phosphor-icons/react";

function ScoreChip({ label, value, category }) {
  const tone = (SCORE_TONE[category] || {})[String(value).toLowerCase()] || "#8A8A8A";
  return (
    <div className="border-2 border-foreground bg-surface p-3">
      <div className="label-mono text-muted-foreground text-[0.65rem]">{label}</div>
      <div className="mt-2 flex items-center gap-2">
        <span className="inline-block w-2.5 h-2.5" style={{ background: tone }} />
        <span className="font-semibold text-sm capitalize">{value}</span>
      </div>
    </div>
  );
}

export default function CompassResult() {
  const { id } = useParams();
  const nav = useNavigate();
  const { reset } = useCompass();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [routing, setRouting] = useState(false);
  const [selectedAlt, setSelectedAlt] = useState(null);

  useEffect(() => {
    api.get(`/compass/assessment/${id}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("No se encontró la evaluación"))
      .finally(() => setLoading(false));
  }, [id]);

  const startRoute = async (overrideBusinessType) => {
    setRouting(true);
    try {
      const body = { assessment_id: id };
      if (overrideBusinessType) body.business_type = overrideBusinessType;
      const { data: pb } = await api.post("/compass/route", body);
      toast.success(`Ruta creada: ${pb.name}`);
      reset();
      nav(`/playbooks/${pb.id}`);
    } catch {
      toast.error("No se pudo crear tu ruta. Intenta de nuevo.");
    } finally { setRouting(false); }
  };

  if (loading) {
    return (
      <CompassLayout testid="page-compass-result">
        <div className="text-sm text-muted-foreground">Cargando tu ruta…</div>
      </CompassLayout>
    );
  }
  if (!data) return null;

  const r = data.result || {};
  const rec = r.recommendation || {};
  const readiness = READINESS_COLORS[r.readiness] || READINESS_COLORS.amarillo;
  const scores = r.scores || {};

  return (
    <CompassLayout back="/brujula" step={4} total={4} testid="page-compass-result">
      <div className="mb-2 label-mono text-muted-foreground flex items-center gap-2">
        <Compass size={14} weight="bold" />
        Tu Ruta Recomendada
      </div>

      <h1 className="display text-3xl md:text-4xl leading-[1]" data-testid="result-title">
        {selectedAlt || rec.business_type || "Ruta"}
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {COUNTRY_FLAGS[data.country] || ""} {data.city || ""}{data.city && data.country ? ", " : ""}{data.country}
      </p>
      {selectedAlt && (
        <div className="mt-3 border-2 border-foreground bg-foreground text-background px-3 py-2 flex items-center gap-2" data-testid="selected-alt-notice">
          <span className="label-mono opacity-80">Ruta alternativa seleccionada</span>
          <span className="text-xs">·</span>
          <button
            onClick={() => setSelectedAlt(null)}
            className="label-mono underline underline-offset-4"
            data-testid="btn-reset-alt"
          >
            Volver a la recomendación principal
          </button>
        </div>
      )}

      {/* Compatibility + Readiness */}
      <div className="grid grid-cols-2 gap-3 mt-5">
        <div className="brut-cta !p-4">
          <div className="label-mono text-muted-foreground">Compatibilidad</div>
          <div className="display text-4xl mt-1" data-testid="result-score">{r.compatibility_score ?? "–"}%</div>
          <div className="mt-2 h-2 border-2 border-foreground">
            <div className="h-full bg-primary" style={{ width: `${r.compatibility_score || 0}%` }} />
          </div>
        </div>
        <div
          className="border-2 border-foreground p-4 flex flex-col justify-between shadow-[4px_4px_0_0_hsl(var(--foreground))]"
          style={{ background: readiness.bg, color: "#fff" }}
          data-testid="result-readiness"
        >
          <div className="label-mono opacity-90">Nivel de preparación</div>
          <div className="display text-xl mt-2 leading-tight">{r.readiness_label || readiness.label}</div>
        </div>
      </div>

      {/* Why */}
      {rec.reasoning && (
        <div className="brut-card mt-5" data-testid="result-reasoning">
          <div className="label-mono text-muted-foreground flex items-center gap-2">
            <Target size={14} weight="bold" /> Por qué esta ruta
          </div>
          <p className="mt-2 text-sm leading-relaxed">{rec.reasoning}</p>
        </div>
      )}

      {/* Scores */}
      <div className="mt-6">
        <div className="label-mono text-muted-foreground mb-2">Evaluación</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="result-scores">
          <ScoreChip label="Potencial de ingresos" value={scores.potencial_ingresos} category="potencial_ingresos" />
          <ScoreChip label="Capital requerido"     value={scores.capital_requerido}  category="capital_requerido" />
          <ScoreChip label="Complejidad"           value={scores.complejidad}        category="complejidad" />
          <ScoreChip label="Riesgo"                value={scores.riesgo}             category="riesgo" />
          <ScoreChip label="Mercado"               value={scores.mercado}            category="mercado" />
          <ScoreChip label="Preparación"           value={scores.preparacion}        category="preparacion" />
        </div>
        {scores.tiempo_ingresos && (
          <div className="mt-3 border-2 border-foreground bg-surface p-3 flex items-center justify-between">
            <div className="label-mono text-muted-foreground">Tiempo para primeros ingresos</div>
            <div className="font-semibold text-sm">{scores.tiempo_ingresos}</div>
          </div>
        )}
      </div>

      {/* Risks */}
      {(r.key_risks || []).length > 0 && (
        <div className="brut-card mt-6" data-testid="result-risks">
          <div className="label-mono text-muted-foreground flex items-center gap-2">
            <Warning size={14} weight="bold" /> Riesgos clave
          </div>
          <ul className="mt-3 space-y-2 text-sm">
            {r.key_risks.map((k, i) => (
              <li key={i} className="flex gap-2">
                <span className="inline-block w-2 h-2 bg-urgent flex-shrink-0 mt-1.5" style={{ background: "#E63946" }} />
                <span>{k}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Blind spots */}
      {(r.blind_spots || []).length > 0 && (
        <div className="brut-card mt-4" data-testid="result-blindspots">
          <div className="label-mono text-muted-foreground flex items-center gap-2">
            <Eye size={14} weight="bold" /> Puntos ciegos
          </div>
          <ul className="mt-3 space-y-2 text-sm">
            {r.blind_spots.map((k, i) => (
              <li key={i} className="flex gap-2">
                <span className="inline-block w-2 h-2 flex-shrink-0 mt-1.5" style={{ background: "#FCA311" }} />
                <span>{k}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Alternatives */}
      {(r.alternative_paths || []).length > 0 && (
        <div className="mt-6" data-testid="result-alternatives">
          <div className="label-mono text-muted-foreground mb-2 flex items-center gap-2">
            <Path size={14} weight="bold" /> Rutas alternativas
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Toca una para elegirla en lugar de la recomendación principal.
          </p>
          <div className="flex flex-col gap-2">
            {r.alternative_paths.map((a, i) => (
              <button
                key={i}
                onClick={() => setSelectedAlt(a.name)}
                data-testid={`alt-${i}`}
                className={`text-left border-2 border-foreground p-3 transition-colors ${
                  selectedAlt === a.name ? "bg-foreground text-background" : "bg-surface hover:bg-muted"
                }`}
              >
                <div className="font-semibold text-sm">{a.name}</div>
                {a.why && <div className={`text-xs mt-1 ${selectedAlt === a.name ? "opacity-80" : "text-muted-foreground"}`}>{a.why}</div>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Insight + next step */}
      {r.founder_insight && (
        <div className="brut-card mt-6" data-testid="result-insight" style={{ borderStyle: "dashed" }}>
          <div className="label-mono text-muted-foreground flex items-center gap-2">
            <Lightbulb size={14} weight="bold" /> Del mentor
          </div>
          <p className="mt-2 text-sm italic leading-relaxed">"{r.founder_insight}"</p>
        </div>
      )}
      {r.next_step && (
        <div className="brut-cta mt-4" data-testid="result-nextstep">
          <div className="label-mono text-muted-foreground flex items-center gap-2">
            <Sparkle size={14} weight="bold" /> Próximo paso hoy
          </div>
          <p className="mt-2 font-semibold text-base leading-snug">{r.next_step}</p>
        </div>
      )}

      {/* Route CTAs */}
      <div className="mt-8 flex flex-col gap-3">
        <button
          disabled={routing}
          onClick={() => startRoute(selectedAlt)}
          data-testid="btn-comenzar-ruta"
          className="btn-primary !py-4 !text-base flex items-center justify-center gap-2"
        >
          {routing
            ? "Preparando tu ruta…"
            : selectedAlt
              ? `Comenzar con ${selectedAlt}`
              : `Comenzar Ruta ${rec.business_type || ""}`}
          <ArrowRight size={16} weight="bold" />
        </button>
        <Link
          to="/brujula"
          className="btn-secondary !py-3 text-center flex items-center justify-center"
          data-testid="btn-brujula-otra-vez"
        >
          Explorar otra opción
        </Link>
      </div>
    </CompassLayout>
  );
}
